// ProductsAdvisorPanel.jsx — per-product AI deep analysis.
// Lets the user pick a product → see its daily history chart + a weekly
// breakdown → click "تحليل ذكي" to get Claude-powered insights and a
// printable PDF report. Powered by /api/products/advisor/* endpoints.
//
// Designed as a clean replacement for the cluttered warnings + tables
// that used to dominate the Products page. All data-quality issues are
// now shown only to admins on the «📋 سجل تحسين النظام» tab.
import React, { useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { mkC } from "./lib/chartFactory";
import { openPrintReport } from "./lib/printReport";
import { formatKSADateTime } from "./lib/ksaTime";

export default function ProductsAdvisorPanel({ user }) {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selected, setSelected] = useState("");
  const [history, setHistory] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  // 1) Load the list once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/products/advisor/list");
        if (!alive) return;
        const items = data?.items || [];
        setProducts(items);
        if (items.length && !selected) setSelected(items[0].name);
      } catch (_e) { /* ignore */ }
      finally { if (alive) setProductsLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) On product change → fetch history + check for a cached analysis
  useEffect(() => {
    if (!selected) return;
    let alive = true;
    setLoadingHist(true);
    setAnalysis(null);
    (async () => {
      try {
        const [{ data: hist }, { data: last }] = await Promise.all([
          api.get(`/products/advisor/history/${encodeURIComponent(selected)}`),
          api.get(`/products/advisor/last/${encodeURIComponent(selected)}`).catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setHistory(hist);
        if (last?.exists) setAnalysis(last);
      } catch (_e) { /* ignore */ }
      finally { if (alive) setLoadingHist(false); }
    })();
    return () => { alive = false; };
  }, [selected]);

  // 3) Charts: per-date line + per-weekday bar
  useEffect(() => {
    if (!history) return;
    const dates = (history.by_date || []).map((d) => d.date);
    const revs = (history.by_date || []).map((d) => d.rev);
    const qtys = (history.by_date || []).map((d) => d.qty);
    if (dates.length) {
      mkC("c-pa-daily", "line", dates, [
        { label: "الإيراد ر.س", data: revs, borderColor: "#7C3AED",
          backgroundColor: "rgba(124,58,237,.12)", fill: true, tension: 0.3, yAxisID: "y" },
        { label: "الكمية", data: qtys, borderColor: "#0891B2",
          backgroundColor: "rgba(8,145,178,.0)", fill: false, tension: 0.3, yAxisID: "y1" },
      ], {
        plugins: { legend: { display: true, position: "bottom" } },
        scales: {
          y:  { position: "right", title: { display: true, text: "إيراد ر.س" } },
          y1: { position: "left", grid: { drawOnChartArea: false }, title: { display: true, text: "كمية" } },
        },
      });
    }
    const wk = history.by_weekday || [];
    if (wk.length) {
      mkC("c-pa-weekday", "bar", wk.map((w) => w.label), [
        { label: "متوسط الإيراد يومياً (ر.س)",
          data: wk.map((w) => Math.round(w.avg_rev_per_day || 0)),
          backgroundColor: wk.map((w) => (w.avg_rev_per_day > 0 ? "rgba(124,58,237,.78)" : "rgba(229,231,235,.6)")),
          borderRadius: 6 },
      ], { plugins: { legend: { display: false } } });
    }
  }, [history]);

  // 4) Selection list (top N by revenue)
  const productOptions = useMemo(() => {
    return products.slice(0, 200).map((p) => ({
      value: p.name,
      label: `${p.name} · ${Math.round(p.total_rev).toLocaleString("en-US")} ر · ${p.days_count} يوم`,
    }));
  }, [products]);

  const analyze = async () => {
    if (!selected) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post("/products/advisor/analyze", { product: selected });
      setAnalysis(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "تعذّر التحليل");
    } finally { setAnalyzing(false); }
  };

  const runBulk = async () => {
    if (!window.confirm("سيتم تحليل أعلى 15 منتجاً بالذكاء الاصطناعي. قد يستغرق الأمر دقيقة. متابعة؟"))
      return;
    setBulkBusy(true);
    setBulk(null);
    try {
      const { data } = await api.post("/products/advisor/analyze-all", { top_n: 15 });
      setBulk(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "فشل التحليل الشامل");
    } finally { setBulkBusy(false); }
  };

  const exportPdf = () => {
    if (!analysis) return;
    const a = analysis.analysis || {};
    const s = analysis.summary || {};
    const sections = [];
    if (a.summary_paragraph) sections.push({ heading: "📝 ملخص تنفيذي", body: a.summary_paragraph });
    if (a.headline) sections.push({ heading: "🎯 العنوان الرئيسي", body: a.headline });
    if (a.strengths?.length) sections.push({ heading: "✅ نقاط القوة", body: a.strengths, kind: "list" });
    if (a.weaknesses?.length) sections.push({ heading: "⚠️ نقاط الضعف", body: a.weaknesses, kind: "list" });
    if (a.best_weekdays?.length) sections.push({ heading: "🌟 أفضل أيام البيع", body: a.best_weekdays, kind: "list" });
    if (a.worst_weekdays?.length) sections.push({ heading: "📉 أسوأ أيام البيع", body: a.worst_weekdays, kind: "list" });
    if (a.recommendations?.length) sections.push({
      heading: "💡 التوصيات",
      body: a.recommendations.map((r) => `• ${r.title}\n   — الإجراء: ${r.action}\n   — الأثر المتوقع: ${r.expected_impact}`),
      kind: "list",
    });
    if (a.combo_suggestions?.length) sections.push({ heading: "🛍 اقتراحات كومبو", body: a.combo_suggestions, kind: "list" });
    openPrintReport({
      title: `تحليل المنتج: ${selected}`,
      subtitle: `الاتجاه: ${a.trend || "—"} · ${s.days || 0} يوم من البيانات`,
      generatedAt: analysis.analyzed_at,
      stats: [
        { label: "إجمالي الإيراد", value: (s.total_rev || 0).toLocaleString("en-US") + " ر.س" },
        { label: "إجمالي الكمية", value: (s.total_qty || 0).toLocaleString("en-US") },
        { label: "متوسط يومي", value: (s.avg_rev_per_day || 0).toLocaleString("en-US") + " ر.س" },
        { label: "هامش الربح", value: s.total_rev ? ((s.total_profit / s.total_rev) * 100).toFixed(1) + "%" : "—" },
      ],
      topRows: (history?.by_weekday || []).filter((w) => w.days_count > 0).map((w) => ({
        name: w.label, code: `${w.days_count} يوم`,
        value: Math.round(w.avg_rev_per_day) + " ر.س/يوم",
        sub: `إجمالي ${Math.round(w.total_rev)} ر`,
      })),
      sections,
      footer: "ثواني · مستشار المنتجات الذكي",
    });
  };

  const exportBulkPdf = () => {
    if (!bulk || !bulk.results) return;
    const successes = bulk.results.filter((r) => r.ok);
    openPrintReport({
      title: "تقرير شامل: تحليل أعلى المنتجات",
      subtitle: `تم تحليل ${bulk.analyzed_count} منتج`,
      generatedAt: bulk.completed_at,
      stats: [
        { label: "تم تحليلها", value: bulk.analyzed_count },
        { label: "فشلت", value: bulk.failed_count || 0 },
      ],
      topRows: successes.map((r) => ({ name: r.product, code: "", value: r.headline || "—" })),
      sections: [{ heading: "ℹ️ ملاحظة", body: "افتح كل منتج على حدة في صفحة المنتجات للحصول على تحليل كامل + توصيات." }],
      footer: "ثواني · تحليل المنتجات الشامل",
    });
  };

  const ana = analysis?.analysis || {};
  const sum = history || {};
  const hasData = (sum.days || 0) > 0;

  return (
    <div data-testid="products-advisor-panel" style={{ marginBottom: 16 }}>
      <div className="card" style={{
        padding: 16,
        background: "linear-gradient(135deg, #fff 0%, #F5F3FF 100%)",
        borderInlineStart: "4px solid #7C3AED",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#4C1D95", marginBottom: 4 }}>
              🤖 مستشار المنتجات الذكي
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
              اختر منتجاً لمشاهدة سجله اليومي وأفضل أيامه، أو اطلب تحليلاً ذكياً كاملاً بتوصيات قابلة للتنفيذ.
            </div>
          </div>
          {isAdmin && (
            <button onClick={runBulk} disabled={bulkBusy} data-testid="pa-bulk-btn"
              style={{
                padding: "8px 14px", background: "#4C1D95", color: "#fff",
                border: "none", borderRadius: 6, fontWeight: 700,
                cursor: bulkBusy ? "wait" : "pointer", fontSize: 12.5,
              }}>
              {bulkBusy ? "⏳ جارٍ التحليل الشامل..." : "📚 تحليل شامل لأعلى 15 منتج"}
            </button>
          )}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: "#4C1D95" }}>🛒 المنتج:</label>
          <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="pa-product-select"
            style={{ flex: 1, minWidth: 240, padding: "8px 10px", border: "1px solid var(--border)",
              borderRadius: 6, fontSize: 13, background: "#fff" }}>
            {productsLoading && <option value="">⏳ جارٍ تحميل المنتجات...</option>}
            {!productsLoading && productOptions.length === 0 && <option value="">— لا توجد منتجات —</option>}
            {productOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
          <button onClick={analyze} disabled={!selected || analyzing} data-testid="pa-analyze-btn"
            style={{
              padding: "8px 14px", background: "#7C3AED", color: "#fff",
              border: "none", borderRadius: 6, fontWeight: 700,
              cursor: (!selected || analyzing) ? "not-allowed" : "pointer", fontSize: 12.5,
            }}>
            {analyzing ? "⏳ يحلّل..." : "🤖 تحليل ذكي"}
          </button>
          {analysis && (
            <button onClick={exportPdf} data-testid="pa-pdf-btn"
              style={{
                padding: "8px 14px", background: "#fff", color: "#4C1D95",
                border: "1px solid #7C3AED", borderRadius: 6, fontWeight: 700,
                cursor: "pointer", fontSize: 12.5,
              }}>
              📄 تنزيل PDF
            </button>
          )}
        </div>

        {/* Summary KPIs */}
        {hasData && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))",
            gap: 10, marginBottom: 12 }}>
            <KPI label="📅 أيام مسجّلة" value={sum.days} sub={`${(sum.by_date?.[0]?.date) || ""} → ${(sum.by_date?.[sum.by_date.length - 1]?.date) || ""}`} />
            <KPI label="📦 إجمالي الكمية" value={(sum.total_qty || 0).toLocaleString("en-US")} sub={`${(sum.avg_qty_per_day || 0).toLocaleString("en-US")} / يوم`} />
            <KPI label="💰 إجمالي الإيراد" value={(sum.total_rev || 0).toLocaleString("en-US") + " ر"} sub={`${(sum.avg_rev_per_day || 0).toLocaleString("en-US")} / يوم`} />
            <KPI label="🌟 أفضل يوم" value={sum.best_day?.weekday || "—"} sub={`${sum.best_day?.date || ""} · ${(sum.best_day?.rev || 0).toLocaleString("en-US")} ر`} />
          </div>
        )}

        {!hasData && !loadingHist && (
          <div style={{ padding: 18, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
            ℹ لا يوجد سجل يومي لهذا المنتج بعد. تأكد من بناء التاريخ اليومي من لوحة «📆 المبيعات اليومية الفعلية».
          </div>
        )}

        {hasData && (
          <div className="g2" style={{ marginBottom: 12 }}>
            <div style={{ background: "#fff", borderRadius: 8, padding: 12, border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#4C1D95", marginBottom: 6 }}>📈 السجل اليومي</div>
              <div className="cw"><canvas id="c-pa-daily" data-testid="pa-chart-daily"></canvas></div>
            </div>
            <div style={{ background: "#fff", borderRadius: 8, padding: 12, border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: "#4C1D95", marginBottom: 6 }}>📅 متوسط الإيراد حسب أيام الأسبوع</div>
              <div className="cw"><canvas id="c-pa-weekday" data-testid="pa-chart-weekday"></canvas></div>
            </div>
          </div>
        )}

        {/* AI Analysis output */}
        {analysis && ana && (
          <div data-testid="pa-analysis-result" style={{ background: "#fff", borderRadius: 8, padding: 14, border: "1px solid #DDD6FE" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#4C1D95", marginBottom: 8 }}>
              🤖 تحليل {selected}
              <span style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 500, marginInlineStart: 8 }}>
                {formatKSADateTime(new Date(analysis.analyzed_at), { dateStyle: "medium", timeStyle: "short" })}
              </span>
            </div>
            {ana.headline && (
              <div style={{ padding: 10, background: "#F5F3FF", borderInlineStart: "3px solid #7C3AED",
                borderRadius: 4, fontSize: 13, fontWeight: 600, color: "#4C1D95", marginBottom: 10 }}>
                {ana.headline}
              </div>
            )}
            <div className="g2" style={{ gap: 10 }}>
              <BoxList title="✅ نقاط القوة" items={ana.strengths} color="#16A34A" />
              <BoxList title="⚠️ نقاط الضعف" items={ana.weaknesses} color="#D97706" />
            </div>
            <div className="g2" style={{ gap: 10, marginTop: 10 }}>
              <BoxList title="🌟 أفضل أيام البيع" items={ana.best_weekdays} color="#0891B2" />
              <BoxList title="📉 أسوأ أيام البيع" items={ana.worst_weekdays} color="#9CA3AF" />
            </div>
            {ana.trend && (
              <div style={{ marginTop: 10, padding: 10, background: "#FEF3C7", borderRadius: 4, fontSize: 12.5, color: "#92400E" }}>
                <b>📊 الاتجاه: {ana.trend}</b>
                {ana.trend_explanation && <span> — {ana.trend_explanation}</span>}
              </div>
            )}
            {ana.recommendations?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#4C1D95", marginBottom: 6 }}>💡 التوصيات</div>
                {ana.recommendations.map((r, i) => (
                  <div key={i} style={{
                    padding: 10, marginBottom: 6, background: "#FAF5FF",
                    borderRadius: 4, border: "1px solid #E9D5FF",
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#4C1D95" }}>{r.title}</div>
                    {r.action && <div style={{ fontSize: 12, color: "#1F2937", marginTop: 2 }}><b>الإجراء:</b> {r.action}</div>}
                    {r.expected_impact && <div style={{ fontSize: 12, color: "#16A34A", marginTop: 2 }}><b>الأثر:</b> {r.expected_impact}</div>}
                  </div>
                ))}
              </div>
            )}
            {ana.combo_suggestions?.length > 0 && (
              <BoxList title="🛍 اقتراحات كومبو" items={ana.combo_suggestions} color="#7C3AED" />
            )}
            {ana.summary_paragraph && (
              <div style={{ marginTop: 12, padding: 10, background: "#F9FAFB", borderRadius: 4,
                fontSize: 12.5, lineHeight: 1.9, color: "#374151" }}>
                {ana.summary_paragraph}
              </div>
            )}
          </div>
        )}

        {/* Bulk result */}
        {bulk && (
          <div style={{ marginTop: 14, padding: 12, background: "#fff", borderRadius: 8, border: "1px solid #DDD6FE" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#4C1D95" }}>
                📚 تحليل شامل: تم تحليل {bulk.analyzed_count} منتج
                {bulk.failed_count > 0 && <span style={{ color: "#DC2626" }}> · {bulk.failed_count} فشل</span>}
              </div>
              <button onClick={exportBulkPdf} data-testid="pa-bulk-pdf-btn"
                style={{ padding: "5px 10px", background: "#7C3AED", color: "#fff",
                  border: "none", borderRadius: 5, fontWeight: 700, fontSize: 11.5, cursor: "pointer" }}>
                📄 تقرير شامل PDF
              </button>
            </div>
            <ul style={{ margin: 0, padding: "0 16px", fontSize: 12, lineHeight: 1.8 }}>
              {bulk.results.filter((r) => r.ok).slice(0, 15).map((r, i) => (
                <li key={i}>
                  <b style={{ cursor: "pointer", color: "#4C1D95" }} onClick={() => setSelected(r.product)}>
                    {r.product}
                  </b> — {r.headline}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, sub }) {
  return (
    <div style={{ padding: 10, background: "#fff", borderRadius: 6, border: "1px solid var(--border)" }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: "#4C1D95", marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function BoxList({ title, items, color }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ background: "#fff", padding: 10, borderRadius: 6, border: `1px solid ${color}33` }}>
      <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 6 }}>{title}</div>
      <ul style={{ margin: 0, padding: "0 16px", fontSize: 12.5, lineHeight: 1.8, color: "#1F2937" }}>
        {items.map((it, i) => (<li key={i}>{it}</li>))}
      </ul>
    </div>
  );
}
